import { Subtenant } from '@bioliberty/lifemind';
import { useEffect, useRef } from 'react';
import { InternalUtilization } from '../../api/internal';
import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from '../../components/Table';
import { cn } from '../../util';
import { ClinicRow, MonthColumn, useUtilizationData } from '../hooks/useUtilizationData';
import { UTILIZATION_METRICS, UtilizationMetric, UtilizationMetricConfig } from './utilizationMetrics';

export interface UtilizationPivotTableProps {
  utilization: InternalUtilization | undefined;
  subtenants: Subtenant[] | undefined;
  metric: UtilizationMetric;
  thresholds: { red: number; amber: number } | undefined;
}

function getRagClasses(
  value: number,
  isPrePilot: boolean,
  thresholds: { red: number; amber: number } | undefined
): string {
  if (isPrePilot) {
    return 'bg-muted/20 text-subtle';
  }
  if (!thresholds) {
    return '';
  }
  if (value <= thresholds.red) {
    return 'bg-red-500/20 text-red-300';
  }
  if (value <= thresholds.amber) {
    return 'bg-amber-500/20 text-amber-300';
  }
  return 'bg-green-500/20 text-green-300';
}

const stickyColumnClasses = 'sticky left-0 z-20 bg-primary';
const stickyHeaderClasses = 'bg-primary sticky top-0 z-10 shadow-md';

export function UtilizationPivotTable({ utilization, subtenants, metric, thresholds }: UtilizationPivotTableProps) {
  const { clinics, months, totals } = useUtilizationData(utilization, subtenants, metric);
  const config = UTILIZATION_METRICS[metric];
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollLeft = scrollRef.current.scrollWidth;
    }
  }, [clinics, months]);

  if (!clinics.length) {
    return <p className="text-subtle py-8 text-center">No utilization data available.</p>;
  }

  return (
    <div ref={scrollRef} className="overflow-auto">
      <Table compact>
        <TableHeader className={stickyHeaderClasses}>
          <TableRow>
            <TableHead className={cn(stickyColumnClasses, 'z-30 min-w-[180px] font-bold')}>Clinic</TableHead>
            {months.map((month) => (
              <TableHead key={month.key} className={cn(config.columnWidthClass, 'text-center whitespace-nowrap')}>
                {month.label}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {clinics.map((clinic) => (
            <ClinicRowComponent
              key={clinic.tenantId}
              clinic={clinic}
              months={months}
              config={config}
              thresholds={thresholds}
            />
          ))}
        </TableBody>
        <TableFooter className={cn(stickyHeaderClasses, 'top-auto bottom-0')}>
          <TableRow>
            <TableCell className={cn(stickyColumnClasses, 'z-30 font-bold')}>Total</TableCell>
            {months.map((month) => {
              const value = totals.get(month.key) ?? 0;
              return (
                <TableCell key={month.key} className="text-center font-bold whitespace-nowrap">
                  {config.format(value)}
                </TableCell>
              );
            })}
          </TableRow>
        </TableFooter>
      </Table>
    </div>
  );
}

function ClinicRowComponent({
  clinic,
  months,
  config,
  thresholds
}: {
  clinic: ClinicRow;
  months: MonthColumn[];
  config: UtilizationMetricConfig;
  thresholds: { red: number; amber: number } | undefined;
}) {
  return (
    <TableRow>
      <TableCell className={cn(stickyColumnClasses, 'font-medium whitespace-nowrap')}>{clinic.label}</TableCell>
      {months.map((month) => {
        const value = clinic.cells.get(month.key) ?? 0;
        const isPrePilot = clinic.firstActiveMonth !== null && month.key < clinic.firstActiveMonth;

        return (
          <TableCell
            key={month.key}
            className={cn('text-center whitespace-nowrap', getRagClasses(value, isPrePilot, thresholds))}
          >
            {isPrePilot ? '–' : config.format(value)}
          </TableCell>
        );
      })}
    </TableRow>
  );
}
